/**
 * useAuthStore.ts - Authentication State Store
 *
 * Issue #1282: Zustand Store分割 Phase 1
 * 認証・プラン・ユーザー状態を管理するStore
 *
 * @module store/useAuthStore
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import logger from '../utils/logger';

// ============================================================
// Types
// ============================================================

export type PlanType = 'free' | 'light' | 'standard' | 'premium';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'manager' | 'staff';
  store: {
    name: string;
    type: string;
  };
}

export interface PlanFeatures {
  aiAnalysis: boolean;
  aiChatLimit: number | null; // token limit for display; enforcement is backend usage API
  benchmarkAccess: boolean;
  advancedReports: boolean;
  customDashboard: boolean;
  apiAccess: boolean;
  voiceInput: boolean;
  // New features
  onboarding: boolean;
  emergency: boolean;
  batchOperations: boolean;
  advancedRecording: boolean;
  inventoryManagement: boolean | 'limited' | 'advanced';
}

// ============================================================
// Constants
// ============================================================

// SSOT: FeatureGate.tsx (FEATURE_PLAN_MAP), plan.types.ts (AI_TOKEN_LIMITS)
export const planFeatureMap: Record<PlanType, PlanFeatures> = {
  // Free tier (ADR-0014 Dashboard-First Architecture)
  free: {
    aiAnalysis: true,
    aiChatLimit: 10000,
    benchmarkAccess: false,
    advancedReports: false,
    customDashboard: false,
    apiAccess: false,
    voiceInput: false,
    onboarding: true,
    emergency: false,
    batchOperations: false,
    advancedRecording: false,
    inventoryManagement: false,
  },
  light: {
    aiAnalysis: true,
    aiChatLimit: 100000,
    benchmarkAccess: false,
    advancedReports: false,
    customDashboard: false,
    apiAccess: false,
    voiceInput: false,
    onboarding: true,
    emergency: false,
    batchOperations: false,
    advancedRecording: false,
    inventoryManagement: false,
  },
  standard: {
    aiAnalysis: true,
    aiChatLimit: 1000000,
    benchmarkAccess: false,
    advancedReports: true,
    customDashboard: false,
    apiAccess: false,
    voiceInput: false,
    onboarding: true,
    emergency: true,
    batchOperations: true,
    advancedRecording: true,
    inventoryManagement: 'limited',
  },
  premium: {
    aiAnalysis: true,
    aiChatLimit: 5000000, // SSOT: backend/src/modules/billing/types/plan.types.ts AI_TOKEN_LIMITS.premium
    benchmarkAccess: true,
    advancedReports: true,
    customDashboard: true,
    apiAccess: true,
    voiceInput: false,
    onboarding: true,
    emergency: true,
    batchOperations: true,
    advancedRecording: true,
    inventoryManagement: 'advanced',
  },
};

// ============================================================
// Feature Access Types
// ============================================================

export type FeatureAccessType =
  | 'aiChat'
  | 'benchmarkAnalysis'
  | 'advancedReports'
  | 'customDashboard'
  | 'apiAccess'
  | 'voiceInput'
  | 'onboarding'
  | 'emergency'
  | 'batchOperations'
  | 'advancedRecording'
  | 'inventoryManagement';

// ============================================================
// Store State Interface
// ============================================================

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  currentPlan: PlanType;
  planFeatures: PlanFeatures;
  aiUsage: Record<string, number>;

  // Actions
  setUser: (user: User | null) => void;
  updateStoreName: (name: string) => void;
  setPlan: (plan: PlanType) => void;
  incrementAIUsage: (feature: string) => void;
  checkFeatureAccess: (feature: FeatureAccessType) => boolean;
  logout: () => void;
}

// ============================================================
// Store Implementation
// ============================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      isAuthenticated: false,
      currentPlan: 'free',
      planFeatures: planFeatureMap.free,
      aiUsage: {},

      // Actions
      setUser: (user) => {
        logger.log('[AuthStore] setUser:', user?.id || 'null');
        set({
          user,
          isAuthenticated: user !== null,
        });
      },

      updateStoreName: (name) => {
        logger.log('[AuthStore] updateStoreName:', name);
        set((state) => ({
          user: state.user
            ? {
                ...state.user,
                store: {
                  ...state.user.store,
                  name,
                },
              }
            : null,
        }));
      },

      setPlan: (plan) => {
        logger.log('[AuthStore] setPlan:', plan);
        set({
          currentPlan: plan,
          planFeatures: planFeatureMap[plan],
        });
      },

      incrementAIUsage: (feature) => {
        set((state) => ({
          aiUsage: {
            ...state.aiUsage,
            [feature]: (state.aiUsage[feature] || 0) + 1,
          },
        }));
      },

      checkFeatureAccess: (feature) => {
        const state = get();
        const { planFeatures } = state;

        switch (feature) {
          case 'aiChat':
            if (!planFeatures.aiAnalysis) return false;
            return true;

          case 'benchmarkAnalysis':
            return planFeatures.benchmarkAccess;

          case 'advancedReports':
            return planFeatures.advancedReports;

          case 'customDashboard':
            return planFeatures.customDashboard;

          case 'apiAccess':
            return planFeatures.apiAccess;

          case 'voiceInput':
            return planFeatures.voiceInput;

          case 'onboarding':
            return planFeatures.onboarding;

          case 'emergency':
            return planFeatures.emergency;

          case 'batchOperations':
            return planFeatures.batchOperations;

          case 'advancedRecording':
            return planFeatures.advancedRecording;

          case 'inventoryManagement':
            return Boolean(planFeatures.inventoryManagement);

          default:
            return true;
        }
      },

      logout: () => {
        logger.log('[AuthStore] logout');
        set({
          user: null,
          isAuthenticated: false,
          currentPlan: 'free',
          planFeatures: planFeatureMap.free,
          aiUsage: {},
        });
      },
    }),
    {
      name: 'ripia-auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentPlan: state.currentPlan,
        planFeatures: state.planFeatures,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('[AuthStore] Rehydration error:', error);
            return;
          }

          if (state?.currentPlan) {
            // Migration: 'basic' -> 'light', 'enterprise' -> 'premium'
            const planMigrationMap: Record<string, PlanType> = {
              basic: 'light',
              enterprise: 'premium',
            };

            const oldPlan = state.currentPlan as string;
            if (planMigrationMap[oldPlan]) {
              const newPlan = planMigrationMap[oldPlan];
              state.currentPlan = newPlan;
              state.planFeatures = planFeatureMap[newPlan];
              console.log(
                `[AuthStore] Plan migrated: ${oldPlan} -> ${newPlan}`
              );
            }
          }
        };
      },
    }
  )
);

// ============================================================
// Selector Hooks (for optimized re-renders)
// ============================================================

export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useCurrentPlan = () => useAuthStore((state) => state.currentPlan);
export const usePlanFeatures = () =>
  useAuthStore((state) => state.planFeatures);

export default useAuthStore;
